import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function HostEmailDialog({ classId, open, onClose }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ to: "", subject: "", message: "" });
  const [tab, setTab] = useState("edit");
  const [busy, setBusy] = useState(false);
  const origin = window.location.origin;

  useEffect(() => {
    if (!open || !classId) return;
    setD(null); setTab("edit");
    api.post(`/admin/classes/${classId}/host-email`, { origin }).then(({ data }) => {
      setD(data); setForm({ to: data.to || "", subject: data.subject, message: data.message });
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [open, classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = async () => {
    setTab("preview");
    const { data } = await api.post(`/admin/classes/${classId}/host-email`, { origin, ...form });
    setD((prev) => ({ ...prev, html: data.html }));
  };
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/classes/${classId}/host-email`, { origin, ...form, send: true });
      toast.success(`Email sent to ${form.to}`);
      onClose(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const input = "w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none text-sm";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="host-email-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Email the host</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-stone-500">Preparing email…</p> : (
          <div className="space-y-3">
            {(d.warnings || []).map((w) => (
              <p key={w} data-testid="host-email-warning" className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" /> {w}</p>
            ))}
            <div className="flex gap-2">
              {["edit", "preview"].map((t) => (
                <button key={t} data-testid={`host-email-tab-${t}`} onClick={() => (t === "preview" ? preview() : setTab("edit"))}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border ${tab === t ? "bg-[#121212] text-white border-[#121212]" : "border-stone-300"}`}>{t}</button>
              ))}
            </div>
            {tab === "edit" ? (
              <>
                <label className="block text-xs uppercase tracking-wider text-stone-500">To (host email)
                  <input data-testid="host-email-to" type="email" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="host@example.com" className={input + " mt-1"} /></label>
                <label className="block text-xs uppercase tracking-wider text-stone-500">Subject
                  <input data-testid="host-email-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={input + " mt-1"} /></label>
                <label className="block text-xs uppercase tracking-wider text-stone-500">Message
                  <textarea data-testid="host-email-message" rows={12} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={input + " mt-1 font-sans normal-case tracking-normal"} /></label>
                <p className="text-xs text-stone-500">The guest booking link and RSVP tracker buttons are added below your message automatically.</p>
              </>
            ) : (
              <iframe title="Host email preview" data-testid="host-email-preview" srcDoc={d.html} className="w-full h-[55vh] border border-stone-200 rounded-lg bg-white" />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="host-email-cancel" onClick={() => onClose()} className="px-5 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
              <button data-testid="host-email-send" onClick={send} disabled={busy || !form.to.trim()} className="px-5 py-2 rounded-full bg-[#121212] text-[#C6FF33] text-sm font-bold inline-flex items-center gap-2 disabled:opacity-40"><Send size={14} /> {busy ? "Sending…" : "Send email"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
